import Api from '../Api';

/** Как в `AddOrganization` / `DaysSection` */
export const organizationFormDays = [
  { name: 'Понедельник', value: 'monday' },
  { name: 'Вторник', value: 'tuesday' },
  { name: 'Среда', value: 'wednesday' },
  { name: 'Четверг', value: 'thursday' },
  { name: 'Пятница', value: 'friday' },
  { name: 'Суббота', value: 'saturday' },
  { name: 'Воскресенье', value: 'sunday' },
];

const DAY_KEYS = organizationFormDays.map((d) => d.value);

const defaultSchedule = organizationFormDays.reduce(
  (acc, day) => ({ ...acc, [day.value]: { start: '', end: '' } }),
  {},
);

export function stripJsonFromPastedText(raw) {
  if (typeof raw !== 'string') return '';
  const t = raw.trim();
  const fenced = t.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  return t;
}

/**
 * Загружает категории, страны, языки и все города по странам (как на форме выбора города).
 */
export async function fetchAllStaticDataForAutofill() {
  const [{ data: catData }, { data: countriesData }, { data: langData }] = await Promise.all([
    Api.getCategories(),
    Api.getCountries(),
    Api.getLanguages(),
  ]);

  const categories = catData?.categories || [];
  const countries = countriesData?.countries || [];
  const languages = langData?.languages || [];

  const citiesByCountryId = {};
  await Promise.all(
    countries.map(async (country) => {
      const { data } = await Api.getCities({ country_id: country._id });
      citiesByCountryId[country._id] = data?.cities || [];
    }),
  );

  return {
    categories,
    countries,
    languages,
    citiesByCountryId,
  };
}

/**
 * Общие справочники для общего файла автозаполнения организации (без городов).
 */
export async function fetchOrganizationAutofillCommonData() {
  const [{ data: catData }, { data: langData }] = await Promise.all([
    Api.getCategories(),
    Api.getLanguages(),
  ]);
  return {
    categories: catData?.categories || [],
    languages: langData?.languages || [],
  };
}

/**
 * Только город выбранной страны для второго скачиваемого файла.
 */
export async function fetchOrganizationAutofillCitiesData(countryId) {
  if (!countryId) {
    throw new Error('countryId is required');
  }
  const [{ data: countriesData }, { data: citiesData }] = await Promise.all([
    Api.getCountries(),
    Api.getCities({ country_id: countryId }),
  ]);

  const countries = countriesData?.countries || [];
  const country = countries.find((c) => c._id === countryId);
  if (!country) {
    throw new Error('Country not found');
  }

  const cities = citiesData?.cities || [];
  return { selectedCountry: country, cities };
}

/**
 * Справочник для «Преобразовать»: категории, языки, города только выбранной страны.
 * В citiesByCountryId — один ключ (countryId).
 */
export async function fetchAutofillDataForSelectedCountry(countryId) {
  if (!countryId) {
    throw new Error('countryId is required');
  }
  const [{ data: catData }, { data: countriesData }, { data: langData }, { data: citiesData }] = await Promise.all([
    Api.getCategories(),
    Api.getCountries(),
    Api.getLanguages(),
    Api.getCities({ country_id: countryId }),
  ]);

  const countries = countriesData?.countries || [];
  const country = countries.find((c) => c._id === countryId);
  if (!country) {
    throw new Error('Country not found');
  }

  const cities = citiesData?.cities || [];

  return {
    categories: catData?.categories || [],
    languages: langData?.languages || [],
    selectedCountry: country,
    citiesByCountryId: { [countryId]: cities },
  };
}

function findCategoryById(categories, categoryId) {
  if (!categoryId) return null;
  return categories.find((c) => c._id === categoryId) || null;
}

function resolveSubCategoriesByIds(category, subCategoryIds) {
  if (!category?.sub_categories?.length || !Array.isArray(subCategoryIds) || !subCategoryIds.length) {
    return [];
  }
  const subs = category.sub_categories;
  return subs.filter((s) => subCategoryIds.includes(s._id));
}

export function findCityById(citiesByCountryId, cityId) {
  if (!cityId) return null;
  let found = null;
  Object.keys(citiesByCountryId).forEach((cid) => {
    if (found) return;
    const list = citiesByCountryId[cid];
    const f = list.find((c) => c._id === cityId);
    if (f) found = f;
  });
  return found;
}

function resolveLanguagesByIds(languages, languageIds) {
  if (!Array.isArray(languageIds) || !languageIds.length) return [];
  return languages.filter((l) => languageIds.includes(l._id));
}

function normalizeDayValue(val) {
  if (val === undefined || val === null) return null;

  if (typeof val === 'string') {
    const s = val.trim();
    if (s.includes('-') && /^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(s.replace(/\s/g, ''))) {
      const clean = s.replace(/\s/g, '');
      const [start, end] = clean.split('-');
      return { type: 'hours', start, end };
    }
    return { type: s, start: '', end: '' };
  }

  if (typeof val === 'object' && val.type === 'hours') {
    return {
      type: 'hours',
      start: val.start != null ? String(val.start) : '',
      end: val.end != null ? String(val.end) : '',
    };
  }

  if (typeof val === 'object' && val.type) {
    return { type: val.type, start: '', end: '' };
  }

  return null;
}

/** Только дни, явно присутствующие в объекте schedule */
function normalizeScheduleInputPartial(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(raw, day)) return;
    const normalized = normalizeDayValue(raw[day]);
    if (normalized) out[day] = normalized;
  });
  return Object.keys(out).length ? out : null;
}

/** Только дни из work_time, как в API (частичное слияние с формой). */
export function scheduleFromWorkTimePartial(workTime) {
  if (!workTime || typeof workTime !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(workTime, day)) return;
    const thisDayType = workTime[day];
    if (typeof thisDayType === 'string') {
      if (thisDayType.length === 11 && thisDayType.includes('-')) {
        const [start, end] = thisDayType.split('-');
        out[day] = { start, end, type: 'hours' };
      } else {
        out[day] = { start: '', end: '', type: thisDayType };
      }
    }
  });
  return Object.keys(out).length ? out : null;
}

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];

/** Телефонные поля: в форме только цифры (без +, пробелов, скобок, дефисов). */
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

export function normalizeContactDigits(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/\D/g, '');
}

/**
 * Разбор JSON и сопоставление со справочниками.
 * Возвращает только поля, явно присутствующие в JSON (частичное заполнение).
 */
export function parseOrganizationAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  /** Ключ есть и значение не null — null значит «поле не трогать, не валидировать» */
  const take = (k) => has(k) && data[k] !== null;
  const categoryInJson = take('category_id');

  const {
    categories,
    languages,
    citiesByCountryId,
  } = staticData;

  const patches = {};

  if (take('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (take('name')) patches.name = String(data.name).trim();
  if (take('specialization')) patches.specialization = String(data.specialization).trim();
  if (take('address')) patches.address = String(data.address).trim();
  if (take('description')) patches.description = String(data.description).trim();

  if (take('schedule')) {
    const partial = normalizeScheduleInputPartial(data.schedule);
    if (partial) patches.schedulePartial = partial;
  } else if (take('work_time')) {
    const partial = scheduleFromWorkTimePartial(data.work_time);
    if (partial) patches.schedulePartial = partial;
  }

  if (take('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k) && data.contacts[k] !== null) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  let effectiveCategory = null;
  if (categoryInJson) {
    effectiveCategory = findCategoryById(categories, data.category_id);
    if (!effectiveCategory && data.category_id) {
      return { error: 'Категория не найдена по category_id.' };
    }
    patches.category = effectiveCategory;
  }

  if (take('sub_category_ids')) {
    if (!effectiveCategory) {
      return { error: 'Укажите category_id вместе с sub_category_ids.' };
    }
    const subIds = Array.isArray(data.sub_category_ids)
      ? data.sub_category_ids.filter((id) => id != null)
      : [];
    patches.subCategories = resolveSubCategoriesByIds(effectiveCategory, subIds);
  }

  if (categoryInJson && effectiveCategory?.is_online) {
    patches.city = null;
    patches.additionalCities = [];
    if (take('address')) patches.address = '';
    if (take('coordinates')) patches.coordinates = null;
  } else {
    if (take('city_id')) {
      const city = findCityById(citiesByCountryId, data.city_id);
      if (!city) {
        return { error: 'Город не найден по city_id.' };
      }
      patches.city = city;
    }

    if (take('additional_city_ids')) {
      const ids = Array.isArray(data.additional_city_ids) ? data.additional_city_ids : [];
      patches.additionalCities = ids
        .filter((id) => id != null)
        .map((id) => findCityById(citiesByCountryId, id))
        .filter(Boolean);
    }

    if (take('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
      patches.coordinates = {
        lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
        lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
        is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
      };
    }
  }

  if (take('language_ids')) {
    const langIds = Array.isArray(data.language_ids)
      ? data.language_ids.filter((id) => id != null)
      : [];
    patches.languages = resolveLanguagesByIds(languages, langIds);
  }

  if (take('tags')) {
    patches.tags = Array.isArray(data.tags)
      ? data.tags.filter((t) => t != null).map((t) => String(t).trim()).filter(Boolean)
      : [];
  }

  if (take('template_photos_ids')) {
    patches.templatePhotoIds = Array.isArray(data.template_photos_ids)
      ? data.template_photos_ids.filter((id) => typeof id === 'string' && id.length === 24)
      : [];
  }

  return { patches };
}

/** Схема подсказки и поля — для раздельных файлов REFERENCE_COMMON + REFERENCE_CITIES. */
function buildOrganizationAutofillSchemaDescription() {
  return {
    description: 'Один JSON-объект для формы «Добавить предложение». Человек даёт два текстовых блока в одном сообщении: (1) общий файл без городов — там REFERENCE_COMMON (categories, languages); (2) файл городов одной страны — REFERENCE_CITIES (selected_country, cities). Одна и та же страна по городам для задачи. В categories у записей есть is_online: при совпадении name выбирай ветку онлайн/офлайн по смыслу текста. Поля опускай, если в тексте нет данных. В JSON — только _id из справочников.',
    fields: {
      owner_id: '24-символьный hex ID владельца в системе',
      name: 'Название предложения',
      category_id: 'строка — ровно categories[i]._id из REFERENCE_COMMON.categories. Смотри categories[i].is_online: при дублях name выбери режим по тексту (адрес/город/офис → is_online: false; только дистанционно без точки → true; смешанно с адресом → обычно офлайн).',
      sub_category_ids: ['массив строк — только sub_categories[j]._id выбранной category_id (той же ветки онлайн/офлайн)'],
      specialization: 'строка',
      city_id: 'строка — ровно cities[i]._id из REFERENCE_CITIES.cities (города только выбранной в файле 2 страны, см. REFERENCE_CITIES.selected_country)',
      additional_city_ids: ['массив строк — только _id из REFERENCE_CITIES.cities; все те же selected_country'],
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      schedule: 'объект дней monday..sunday; каждый день: { type: "hours", start, end } или { type: "day_off"|"temp_closed"|"whole_day" } или строка "09:00-18:00" / "day_off"',
      work_time: 'альтернатива schedule в формате API: monday: "09:00-18:00" или "day_off"',
      contacts: 'Объект: phone_number, whatsapp, viber — только цифры подряд (если в тексте +7 (999) 123-45-67, в JSON пиши 79991234567). instagram, telegram, facebook, website — как строки без изменения формата.',
      description: 'строка',
      language_ids: 'ОБЯЗАТЕЛЬНО: массив строк, каждая = languages[n]._id из REFERENCE_COMMON.languages. ЗАПРЕЩЕНО класть человекочитаемые названия языков — только id.',
      tags: 'массив коротких строк — ключевые слова/темы по тексту; не из справочника id. Если из текста можно вывести хотя бы одну тему — заполни минимум 1–3 тега, не оставляй пустым без причины.',
      template_photos_ids: ['опционально _id шаблонных фото'],
    },
    json_syntax: 'Ответ одним сообщением: ТОЛЬКО markdown-блок с языком json, внутри — один валидный объект как после JSON.stringify (допустимы отступы как JSON.stringify(obj, null, 2)). Никакого текста до ```json и после закрывающего ```. В строковых полях внутренние кавычки " только как \\"; без сырых переводов строк там, где ломается синтаксис JSON. Админка извлекает текст из блока и передаёт в JSON.parse.',
    note: 'Категория: различай categories с is_online по смыслу. Для онлайн-категории (is_online: true) не включай city_id / additional_city_ids. Телефоны в contacts.phone_number, whatsapp, viber — только цифры, без +, скобок и разделителей.',
  };
}

function organizationAutofillPromptModelBody() {
  const schemaDescription = buildOrganizationAutofillSchemaDescription();
  return [
    'Ты — помощник по заполнению карточки предложения в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала человек пришлёт тебе в ОДНОМ сообщении ДВА фрагмента подряд: (А) общий файл со всей инструкцией и списками categories и languages (REFERENCE_COMMON внизу фрагмента А); (Б) отдельный короткий файл только с городами одной страны (REFERENCE_CITIES). На всё первое сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь ровно этой фразой и больше ничего: «Задача ясна, отправь текст для преобразования». Дальше жди следующих сообщений.',
    '— Когда в следующих сообщениях пришлют обычный текст о предложении (описание, контакты, режим и т.д.), выполни шаги ниже и верни результат.',
    '',
    'Справочник разбит так: REFERENCE_COMMON (файл А) содержит categories и languages (у категорий _id, name, is_online; подкатегории для category_id — внутри categories). REFERENCE_CITIES (файл Б) содержит selected_country и массив cities только этой страны — для city_id и additional_city_ids.',
    '',
    'КРИТИЧНО — категория: онлайн или офлайн (categories[].is_online):',
    '— В REFERENCE_COMMON часто есть две записи с одинаковым или почти одинаковым name: одна с is_online: true, другая с is_online: false. В JSON category_id и sub_category_ids должны быть из ОДНОЙ ветки — той, что соответствует реальному предложению.',
    '— Офлайн (is_online: false): явный физический адрес, город, офис, филиал, «приходите к нам», график работы на месте — выбирай категорию с is_online: false и при необходимости заполняй city_id из REFERENCE_CITIES и address.',
    '— Онлайн (is_online: true): услуги только дистанционно, без физической точки в тексте, «работаем онлайн / по всей стране или миру», нет места посещения — выбирай категорию с is_online: true; city_id и additional_city_ids не включай.',
    '— Если в тексте смешано: указан конкретный адрес или город офиса и при этом «онлайн-консультации», по умолчанию считай предложение офлайн-присутствием (адрес/город), если только явным образом не сказано, что это только виртуальный бренд без офиса.',
    '— sub_category_ids подбирай только из sub_categories той записи категории, чей _id ты поставил в category_id.',
    '',
    'КРИТИЧНО — города (city_id, additional_city_ids):',
    '— Используй ТОЛЬКО REFERENCE_CITIES.cities из второго приложенного файла; каждый _id должен буквально быть в этом массиве.',
    '— Не подставляй город другой страны или id, которого нет в списке городов приложения для этой задачи.',
    '',
    'КРИТИЧНО — языки (language_ids):',
    '— Данные языков только в REFERENCE_COMMON.languages. В JSON массив language_ids — только строки-_id этих объектов.',
    '— ЗАПРЕЩЕНО писать в language_ids человекочитаемые названия («Русский», «English», «Հայերեն» и т.п.).',
    '— Алгоритм: по тексту понял язык → нашёл объект в languages с подходящим name → в language_ids только его _id.',
    '— Пример правильно: "language_ids": ["507f1f77bcf86cd799439011", "507f191e810c19729de860ea"]',
    '— Пример НЕПРАВИЛЬНО: "language_ids": ["Русский", "English"]',
    '',
    'КРИТИЧНО — теги (tags):',
    '— Массив обычных строк из смысла текста (не из справочника id).',
    '— Минимум 1–3 тега, если в тексте есть предметная информация.',
    '',
    'КРИТИЧНО — внутренние двойные кавычки в тексте:',
    '— Лишняя " внутри строкового значения — только экранирование как \\" (JSON.stringify).',
    '— Пример эскейпа внутри строки значения адреса: подстроку с именем в кавычках пиши как \\"Имя\\" между обычным текстом.',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— Только цифры 0–9 подряд.',
    '— Если в тексте +7 (999) 123-45-67 — в JSON только цифры в том же порядке по смыслу.',
    '— instagram, telegram, facebook, website не режь до цифр.',
    '',
    'КРИТИЧНО — формат ответа:',
    '— Всегда возвращай результат ТОЛЬКО внутри блока кода с типом json:',
    '```json',
    '{...}',
    '```',
    '— Без текста до ```json и после закрывающих ```.',
    '— Внутри — один валидный JSON: без ** и markdown вне строк, одинарные кавычки у ключей запрещены, экранируй ", неэкранированные переводы внутри строк — недопустимы.',
    '— Проверка: между ```json и ``` только JSON для JSON.parse.',
    '',
    'Когда пришёл текст для преобразования, твоя задача:',
    '1) Извлеки из него всё, что относится к полям из schemaDescription.fields.',
    '2) category_id / sub_category_ids — только из REFERENCE_COMMON.categories (с проверкой is_online). language_ids — REFERENCE_COMMON.languages. Города — только REFERENCE_CITIES.cities.',
    '3) Не выдумывай id: каждый id должен буквально совпадать с _id из соответствующего списка.',
    '4) График (schedule): для каждого дня monday..sunday — часы или day_off и т.д.',
    '5) Ответ только одним блоком ```json … ```.',
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
    '',
    'REFERENCE_COMMON:',
  ];
}

/**
 * Файл 1: общий для всех стран — инструкция, categories, languages (без городов).
 */
export function buildOrganizationAutofillCommonFileContent(staticPayload) {
  const { categories, languages } = staticPayload;

  const referenceCommon = {
    categories: categories.map((c) => ({
      _id: c._id,
      name: c.name,
      is_online: Boolean(c.is_online),
      sub_categories: (c.sub_categories || []).map((s) => ({ _id: s._id, name: s.name })),
    })),
    languages: languages.map((l) => ({ _id: l._id, name: l.name })),
  };

  const promptLines = [...organizationAutofillPromptModelBody(), JSON.stringify(referenceCommon, null, 2)];

  const prompt = promptLines.join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение предложения: ОБЩИЙ файл (без городов)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Этот файл одинаковый для любой страны: категории, языки и полная инструкция для модели.',
    '2) Отдельно для каждой нужной страны скачайте второй файл — только список городов этой страны.',
    '3) Первым сообщением в чат с моделью вставьте ОБА файла подряд в одном сообщении сначала этот общий текст целиком, затем без разрыва (или после пустой строки) файл с городами страны для вашей задачи.',
    '4) Дождитесь от модели фразы «Задача ясна…», затем пришлите текст о предложении.',
    '5) Ответ модели вставьте в «Автозаполнение» и выберите в админке ту же страну, что и во втором (города) файле.',
    '',
    prompt,
  ].join('\n');
}

/**
 * Файл 2: только города выбранной страны; скачивается отдельно на каждую страну, вместе с общим в одном сообщении.
 */
export function buildOrganizationAutofillCitiesFileContent(payload) {
  const { selectedCountry, cities } = payload;
  if (!selectedCountry?._id) {
    throw new Error('selectedCountry is required');
  }
  const countryId = selectedCountry._id;
  const referenceCities = {
    selected_country: { _id: selectedCountry._id, name: selectedCountry.name },
    cities: (cities || []).map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || countryId,
    })),
    note_model: `Города только для страны ${selectedCountry.name}. Остальной справочник и промпт — в общем первом файле того же сообщения.`,
  };

  return [
    '══════════════════════════════════════════════════════════════',
    `NOMAD — автозаполнение предложения: ГОРОДА — ${selectedCountry.name}`,
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '— Это приложение к общему файлу (без городов). Для другой страны скачайте такой же второй файл снова.',
    '— В одном сообщении модели отправьте общий файл, затем этот файл (города нужной страны).',
    '',
    'REFERENCE_CITIES:',
    JSON.stringify(referenceCities, null, 2),
  ].join('\n');
}
